import { Link, useNavigate } from "@tanstack/react-router";
import { Bell, Check, X } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
	EventRowTime,
	EventSourceMark,
	EventSourceTile,
	NOTIFICATION_TILE,
	RowKindLine,
} from "@/components/entity-tabs/events-tab/event-row-parts";
import { InlineMarkdownText } from "@/components/markdown-text";
import { useNotificationPeek } from "@/components/peek/notification-peek-context";
import { parsePeekHref } from "@/components/peek/parse-peek-href";
import { SourceAttributionRow } from "@/components/source-attribution-row";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useOrgContext } from "@/hooks/use-org-context";
import { parseAppHref } from "@/lib/activity-links";
import {
	useApproveRun,
	useConnectorDefinitions,
	useRejectRun,
} from "@/lib/api/connections";
import {
	useDeleteNotification,
	useMarkNotificationRead,
	useRecreateBrowserHandoff,
} from "@/lib/api/notifications";
import { openBrowserHandoffTab } from "@/lib/extension-bridge";
import { notificationSummaryMarkdown } from "@/lib/notification-summary";
import {
	notificationActionLabel,
	notificationKindLabel,
} from "@/lib/notification-target";
import { cn, FILLED_BOX } from "@/lib/utils";
import { type TimelineItem, useAttentionInbox } from "./attention-inbox";

/** Navigate preserving query strings (TanStack `to: full?query` drops `?…`). */
function useOpenAppHref() {
	const navigate = useNavigate();
	return (href: string) => {
		const { pathname, search } = parseAppHref(href);
		void navigate({
			to: pathname as "/",
			search: (search ?? {}) as never,
		});
	};
}

/** Link that accepts a full app-relative href including query. */
function AppHrefLink({
	href,
	className,
	onClick,
	children,
}: {
	href: string;
	className?: string;
	onClick?: (e: React.MouseEvent) => void;
	children: ReactNode;
}) {
	const { pathname, search } = parseAppHref(href);
	return (
		<Link
			to={pathname as "/"}
			search={(search ?? {}) as never}
			className={className}
			onClick={onClick}
		>
			{children}
		</Link>
	);
}

function kindLabel(notificationType?: string): string {
	return notificationType
		? notificationKindLabel(notificationType)
		: "Notification";
}

function ApprovalActions({
	runId,
	notificationId,
	reviewHref,
	reviewTitle,
	choiceField,
	choices,
}: {
	runId: number;
	notificationId: number;
	/** Deep-link to Memory ActionApprovalCard (schema form). */
	reviewHref?: string | null;
	/** Pane header title when the CTA opens the peek pane. */
	reviewTitle?: string;
	/**
	 * A decision with named outcomes rather than approve/reject: one button per
	 * option, each approving with `{ [choiceField]: value }`. Approve/Reject
	 * cannot stand in — the schema REQUIRES this field, so a bare approve would
	 * record an empty answer while reporting success.
	 */
	choiceField?: string | null;
	choices?: Array<{ value: string; label: string }> | null;
}) {
	const { orgContext } = useOrgContext();
	const approveRun = useApproveRun();
	const rejectRun = useRejectRun();
	const markRead = useMarkNotificationRead(orgContext);
	const peek = useNotificationPeek();
	const [resolved, setResolved] = useState<"approved" | "rejected" | null>(
		null,
	);
	const [showReject, setShowReject] = useState(false);
	const [reason, setReason] = useState("");
	const busy = approveRun.isPending || rejectRun.isPending;

	if (resolved === "approved") {
		return (
			<p className="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
				Approved — executing…
			</p>
		);
	}
	if (resolved === "rejected") {
		return (
			<p className="mt-2 text-xs font-medium text-muted-foreground">Rejected</p>
		);
	}

	if (showReject) {
		return (
			<div className="mt-2 flex flex-wrap items-center gap-2">
				<input
					type="text"
					placeholder="Reason (optional)"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					className="h-7 min-w-[10rem] flex-1 rounded-md border bg-background px-2 text-xs"
					onClick={(e) => e.stopPropagation()}
				/>
				<Button
					size="sm"
					variant="outline"
					className="h-7 text-xs"
					disabled={busy}
					onClick={(e) => {
						e.stopPropagation();
						rejectRun.mutate(
							{ run_id: runId, reason: reason.trim() || undefined },
							{
								onSuccess: () => {
									setResolved("rejected");
									markRead.mutate(notificationId);
								},
							},
						);
					}}
				>
					{rejectRun.isPending ? (
						<Spinner size="sm" className="mr-1" />
					) : (
						<X className="mr-1 h-3 w-3" />
					)}
					Reject
				</Button>
				<button
					type="button"
					className="text-xs text-muted-foreground hover:text-foreground"
					onClick={(e) => {
						e.stopPropagation();
						setShowReject(false);
					}}
				>
					Cancel
				</button>
			</div>
		);
	}

	// Named outcomes replace approve/reject entirely — mixing them would offer a
	// bare "Approve" that answers none of the options the question asked.
	if (choiceField && choices && choices.length > 0) {
		return (
			<div className="mt-2 flex flex-wrap items-center gap-2">
				{choices.map((choice) => (
					<Button
						key={choice.value}
						size="sm"
						variant="outline"
						className="h-7 text-xs"
						disabled={busy}
						onClick={(e) => {
							e.stopPropagation();
							approveRun.mutate(
								{ run_id: runId, input: { [choiceField]: choice.value } },
								{
									onSuccess: () => {
										setResolved("approved");
										markRead.mutate(notificationId);
									},
								},
							);
						}}
					>
						{approveRun.isPending ? (
							<Spinner size="sm" className="mr-1" />
						) : null}
						{choice.label}
					</Button>
				))}
				<Button
					size="sm"
					variant="ghost"
					className="h-7 text-xs text-muted-foreground"
					disabled={busy}
					onClick={(e) => {
						e.stopPropagation();
						setShowReject(true);
					}}
				>
					<X className="mr-1 h-3 w-3" />
					Dismiss
				</Button>
			</div>
		);
	}

	return (
		<div className="mt-2 flex flex-wrap items-center gap-2">
			{reviewHref ? (
				// Peek is the default destination — the detail is a preview, not a
				// context switch. The pane carries a destination link for the cases
				// that want the real route. Falls back to navigation when the
				// href isn't previewable or no provider is mounted.
				peek && parsePeekHref(reviewHref) ? (
					<Button
						type="button"
						size="sm"
						variant="outline"
						className="h-7 text-xs"
						onClick={(e) => {
							e.stopPropagation();
							markRead.mutate(notificationId);
							peek.open({
								href: reviewHref,
								title: reviewTitle ?? "Approval",
								runId,
							});
						}}
					>
						Review
					</Button>
				) : (
					<AppHrefLink
						href={reviewHref}
						className={cn(
							"inline-flex h-7 items-center justify-center rounded-md border border-input bg-background px-3 text-xs font-medium  transition-colors hover:bg-accent hover:text-accent-foreground",
						)}
						onClick={(e) => {
							e.stopPropagation();
							markRead.mutate(notificationId);
						}}
					>
						Review
					</AppHrefLink>
				)
			) : null}
			<Button
				size="sm"
				className="h-7 text-xs"
				disabled={busy}
				onClick={(e) => {
					e.stopPropagation();
					approveRun.mutate(
						{ run_id: runId },
						{
							onSuccess: () => {
								setResolved("approved");
								markRead.mutate(notificationId);
							},
						},
					);
				}}
			>
				{approveRun.isPending ? (
					<Spinner size="sm" className="mr-1" />
				) : (
					<Check className="mr-1 h-3 w-3" />
				)}
				Approve
			</Button>
			<Button
				size="sm"
				variant="outline"
				className="h-7 text-xs"
				disabled={busy}
				onClick={(e) => {
					e.stopPropagation();
					setShowReject(true);
				}}
			>
				<X className="mr-1 h-3 w-3" />
				Reject
			</Button>
		</div>
	);
}

/** One attention row: the shared card with its inline actions (approve/deny,
 *  open, browser draft, Done) and how it opens (peek or page). The attention list
 *  and the activity stream's inline items both render this — never a copy. */
export function AttentionRow({
	owner,
	item,
	index,
	onFocus,
	onOpenItem,
	tone = "default",
	rowAction = "open",
	tag,
}: {
	owner: string;
	item: TimelineItem;
	/** Position for the attention list's ↑↓ navigation; unset elsewhere. */
	index?: number;
	onFocus?: () => void;
	/** Override navigation while preserving the shared row and read-state semantics. */
	onOpenItem?: (item: TimelineItem) => void;
	/** Relevant context keeps the shared row while using the Lobu accent signal. */
	tone?: "default" | "relevant";
	/** Lens can keep cards static and reserve navigation for explicit actions. */
	rowAction?: "open" | "none";
	/** Short context label beside the kind (the stream's "This page"). */
	tag?: ReactNode;
}) {
	const openHref = useOpenAppHref();
	const peek = useNotificationPeek();
	const { orgContext } = useOrgContext();
	const { data: connectorDefinitions = [] } = useConnectorDefinitions(
		orgContext.organizationId ?? null,
	);
	const markRead = useMarkNotificationRead(orgContext);
	const deleteNotification = useDeleteNotification(orgContext);
	const recreateBrowserHandoff = useRecreateBrowserHandoff(orgContext);
	// Narrow the pending flag to the draft it was fired for, so a recreate in
	// flight never disables (or spins) another draft card.
	const recreatingNotificationId =
		recreateBrowserHandoff.isPending &&
		typeof recreateBrowserHandoff.variables === "number"
			? recreateBrowserHandoff.variables
			: null;
	const rowsOpen = rowAction === "open";
	const openPeekItem = (item: TimelineItem) => {
		if (!item.browserUrl && item.notificationId != null && item.unread) {
			markRead.mutate(item.notificationId);
		}
		// Connection settings already own live authorization and recovery state.
		// Open that page instead of freezing a second copy in the generic peek.
		if (item.interactionType === "authorization" && item.href) {
			openHref(item.href);
			return;
		}
		if (onOpenItem) {
			onOpenItem(item);
			return;
		}
		// Prefer the right-side peek pane when the item is previewable and a
		// provider is present (workspace surfaces). Event/run targets are URL-backed;
		// generic notifications keep their complete row snapshot local because the
		// API has no exact notification read. Browser handoffs use a separate direct
		// provider action; this pane is their explicit Why/how destination.
		if (peek) {
			peek.open({
				href: item.href ?? "",
				title: item.title,
				runId: item.runId ?? undefined,
				body: item.body,
				notificationType: item.notification?.type,
				createdAt: item.notification?.created_at,
				browserUrl: item.browserUrl ?? null,
				browserHandoff: item.browserHandoff ?? null,
				owner,
				sourceAttribution: item.notification ?? null,
			});
			return;
		}
		if (item.href) openHref(item.href);
	};

	const openBrowserItem = async (item: TimelineItem) => {
		if (!item.browserUrl) return;
		const canRecreate =
			item.browserHandoff?.state === "expired" &&
			item.browserHandoff.run_id != null;
		// Keep the handoff unread until the user dismisses it. Opening the page
		// can still fail during preparation, and that failure must stay visible.
		if (
			item.browserHandoff?.state === "completed" ||
			item.browserHandoff?.state === "preparing"
		) {
			toast.info(
				item.browserHandoff.state === "preparing"
					? "Preparing the draft in your browser. Keep that tab open."
					: "Draft prepared. Review and post in your browser, then mark it Done.",
			);
			return;
		}
		if (canRecreate && item.notificationId == null) {
			toast.error("This expired draft cannot be recreated from this card.");
			return;
		}
		try {
			const notificationId = item.notificationId;
			const prepare =
				canRecreate && notificationId != null
					? () => recreateBrowserHandoff.mutateAsync(notificationId)
					: undefined;
			const opened = prepare
				? await openBrowserHandoffTab(item.browserUrl, prepare)
				: await openBrowserHandoffTab(item.browserUrl);
			if (!opened) {
				toast.error(
					"Chrome blocked the handoff tab. Allow pop-ups for Lobu and try again.",
				);
			}
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Could not recreate this browser draft.",
			);
		}
	};

	const openItem = (item: TimelineItem) => {
		if (item.browserUrl) {
			void openBrowserItem(item);
			return;
		}
		openPeekItem(item);
	};

	const approvalRunId = item.approvalRunId ?? null;
	// Gate action UI on the card's interaction kind — the generic
	// contract — not the notification type. A future interaction kind
	// adds its own branch here without changing the card shape.
	const isApprovalInteraction = item.interactionType === "approval";
	// interaction_status is NOT a fixed enum (contract: treat any value
	// not explicitly handled as non-actionable). Every resolved
	// non-pending status renders as a neutral outcome chip — an unknown
	// value like 'auto' must never fall through to an actionable CTA.
	const approvalOutcome = isApprovalInteraction
		? item.interactionStatus === "approved"
			? "Approved"
			: item.interactionStatus === "rejected"
				? "Rejected"
				: item.interactionStatus === "expired"
					? "Expired"
					: item.interactionStatus && item.interactionStatus !== "pending"
						? item.interactionStatus.charAt(0).toUpperCase() +
							item.interactionStatus.slice(1)
						: null
		: null;
	const showInlineApproval =
		isApprovalInteraction &&
		item.interactionStatus === "pending" &&
		item.interactionInline === true &&
		approvalRunId != null;
	const actionLabel =
		!showInlineApproval && !approvalOutcome && item.notification
			? notificationActionLabel(item.notification.type, item.href)
			: null;
	const itemKindLabel =
		isApprovalInteraction && approvalOutcome
			? "Approval"
			: kindLabel(item.notification?.type);

	const summaryText = notificationSummaryMarkdown(item.body);
	const source = item.notification;
	const summary = summaryText ? (
		// Summary only — the detail list and review link live in the
		// focused/peek view. Links inside stop propagation so they
		// don't also trigger the row's open-peek handler.
		// biome-ignore lint/a11y/noStaticElementInteractions: wrapper only stops bubbling from nested links
		// biome-ignore lint/a11y/useKeyWithClickEvents: no independent interaction; the row handles keyboard
		<div
			className="line-clamp-2 text-sm"
			onClick={(e) => {
				if ((e.target as HTMLElement).closest("a")) {
					e.stopPropagation();
				}
			}}
		>
			<InlineMarkdownText
				text={summaryText}
				// Approval bodies are server-composed and carry our own
				// entity permalinks, so same-origin anchors survive; the
				// renderer drops external ones either way. Every other type
				// — above all agent_message, whose body an agent supplies
				// verbatim — renders link text with no anchor at all.
				linkPolicy={
					item.notification?.type === "action_approval_needed"
						? "app-links"
						: "strip"
				}
			/>
		</div>
	) : item.body ? (
		<p className="line-clamp-2 text-sm">{item.body}</p>
	) : null;

	const actions = (
		<>
			<RowKindLine
				dot={
					item.unread
						? tone === "relevant"
							? "bg-primary"
							: "bg-blue-500"
						: undefined
				}
				tag={tag}
			>
				{itemKindLabel}
			</RowKindLine>
			{showInlineApproval && item.notification && approvalRunId != null ? (
				<ApprovalActions
					runId={approvalRunId}
					notificationId={item.notification.id}
					reviewHref={item.href}
					reviewTitle={item.title}
					choiceField={item.interactionChoiceField}
					choices={item.interactionChoices}
				/>
			) : approvalOutcome ? (
				<p
					className={cn(
						"mt-2 text-xs font-medium",
						item.interactionStatus === "approved"
							? "text-emerald-600 dark:text-emerald-400"
							: "text-muted-foreground",
					)}
				>
					{approvalOutcome}
				</p>
			) : null}
			{actionLabel && item.href ? (
				<div className="mt-2">
					<AppHrefLink
						href={item.href}
						className="inline-flex h-7 items-center justify-center rounded-md border border-input bg-background px-3 text-xs font-medium  transition-colors hover:bg-accent hover:text-accent-foreground"
						onClick={(e) => {
							e.stopPropagation();
							if (item.notification && !item.notification.is_read) {
								markRead.mutate(item.notification.id);
							}
							if (
								item.notification?.type === "action_approval_needed" &&
								peek &&
								parsePeekHref(item.href ?? "")
							) {
								e.preventDefault();
								openItem(item);
							}
						}}
					>
						{actionLabel}
					</AppHrefLink>
				</div>
			) : null}
			{item.browserUrl ? (
				<div className="mt-2 space-y-1.5">
					{item.browserHandoff?.state === "expired" ? (
						<p className="text-xs text-amber-700 dark:text-amber-300">
							Draft expired
							{item.browserHandoff.error_message
								? ` — ${item.browserHandoff.error_message}`
								: "."}
						</p>
					) : null}
					<div className="flex flex-wrap items-center gap-2">
						{item.browserHandoff?.state !== "completed" &&
						item.browserHandoff?.state !== "preparing" ? (
							<Button
								type="button"
								size="sm"
								variant="outline"
								className="h-7 text-xs"
								disabled={
									recreatingNotificationId != null &&
									recreatingNotificationId === item.notificationId
								}
								onClick={(event) => {
									event.stopPropagation();
									void openBrowserItem(item);
								}}
							>
								{recreatingNotificationId != null &&
								recreatingNotificationId === item.notificationId ? (
									<Spinner size="sm" className="mr-1" />
								) : null}
								{item.browserHandoff?.state === "expired" &&
								item.browserHandoff.run_id != null
									? "Recreate draft"
									: item.browserHandoff?.state === "expired"
										? "Open page without draft"
										: "Open page"}
							</Button>
						) : (
							<span className="self-center text-xs text-muted-foreground">
								{item.browserHandoff?.state === "preparing"
									? "Preparing draft — keep your browser tab open."
									: "Draft prepared — review and post in your browser."}
							</span>
						)}
						{peek ? (
							<Button
								type="button"
								size="sm"
								variant="ghost"
								className="h-7 text-xs text-muted-foreground"
								onClick={(event) => {
									event.stopPropagation();
									openPeekItem(item);
								}}
							>
								Why/how
							</Button>
						) : null}
						{/*
							Done dismisses the browser-handoff card by deleting its
							notification target. A pending ask/approval has an
							unresolved decision, so it must not be dismissible — the
							decision flow owns the card until it resolves.
						*/}
						{item.interactionStatus !== "pending" ? (
							<Button
								type="button"
								size="sm"
								variant="ghost"
								className="h-7 text-xs text-muted-foreground"
								disabled={deleteNotification.isPending}
								onClick={(event) => {
									event.stopPropagation();
									if (item.notificationId != null) {
										deleteNotification.mutate(item.notificationId);
									}
								}}
							>
								<X className="mr-1 h-3 w-3" />
								Done
							</Button>
						) : null}
					</div>
				</div>
			) : null}
		</>
	);

	// The row reads like an event in the stream — the same source tile, title
	// and time, summary and attribution chips — with a blue tile so it is
	// plainly a notification.
	const body = (
		<div className="flex gap-3">
			<EventSourceTile className={NOTIFICATION_TILE}>
				<EventSourceMark
					source={source}
					connectorDefinitions={connectorDefinitions}
					label={itemKindLabel}
					fallback={<Bell className="h-4 w-4" />}
				/>
			</EventSourceTile>
			<div className="min-w-0 flex-1">
				<div className="flex items-start justify-between gap-3">
					<h4 className="line-clamp-2 min-w-0 text-sm font-medium">
						{item.title}
					</h4>
					<EventRowTime at={new Date(item.at)} />
				</div>
				{summary ? (
					<div className="mt-1 text-muted-foreground">{summary}</div>
				) : null}
				{source ? (
					<SourceAttributionRow
						owner={owner}
						source={source}
						connectorDefinitions={connectorDefinitions}
						variant="chips"
						className="mt-2 text-xs"
					/>
				) : null}
				{actions}
			</div>
		</div>
	);

	// Div (not <button>/<a>) so nested Review/Reconnect links are valid HTML.
	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: interactive rows receive link semantics; a real anchor cannot contain nested actions
		<div
			data-attention-index={index}
			role={rowsOpen ? "link" : undefined}
			tabIndex={rowsOpen ? 0 : undefined}
			onFocus={rowsOpen ? onFocus : undefined}
			onClick={rowsOpen ? () => openItem(item) : undefined}
			onKeyDown={
				rowsOpen
					? (e) => {
							if (e.target !== e.currentTarget) return;
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								openItem(item);
							}
						}
					: undefined
			}
			className={cn(
				"block w-full rounded-lg p-3 text-left transition-colors",
				rowsOpen && "cursor-pointer hover:bg-accent/40",
			)}
		>
			{body}
		</div>
	);
}

/** The attention list itself: unread/pending rows with inline actions, peek,
 *  and ↑↓/Enter/R keyboard navigation. */
export function AttentionList({
	owner,
	emptyState = null,
	itemFilter,
	onOpenItem,
	ariaLabel = "Needs attention",
	tone = "default",
	rowAction = "open",
}: {
	owner: string;
	/** Shown when nothing needs attention. */
	emptyState?: ReactNode;
	/** Optional shared-row projection used by compact surfaces such as Lens. */
	itemFilter?: (item: TimelineItem) => boolean;
	/** Override navigation while preserving the shared row and read-state semantics. */
	onOpenItem?: (item: TimelineItem) => void;
	ariaLabel?: string;
	/** Relevant context keeps the shared row while using the Lobu accent signal. */
	tone?: "default" | "relevant";
	/** Lens can keep cards static and reserve navigation for explicit actions. */
	rowAction?: "open" | "none";
}) {
	const { session, isLoading, isError, attentionItems, markManyRead } =
		useAttentionInbox(owner);
	const displayedItems = useMemo(
		() => (itemFilter ? attentionItems.filter(itemFilter) : attentionItems),
		[attentionItems, itemFilter],
	);
	const [activeIndex, setActiveIndex] = useState(0);
	const attentionRef = useRef<HTMLElement>(null);
	const rowsOpen = rowAction === "open";

	const focusAttentionItem = (index: number) => {
		const nextIndex = Math.min(Math.max(index, 0), displayedItems.length - 1);
		setActiveIndex(nextIndex);
		attentionRef.current
			?.querySelector<HTMLElement>(`[data-attention-index="${nextIndex}"]`)
			?.focus();
	};

	if (!session) {
		return (
			<p className="py-6 text-center text-sm text-muted-foreground">
				Sign in to see workspace activity.
			</p>
		);
	}

	if (isLoading && displayedItems.length === 0) {
		return (
			<div className="space-y-2">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
						key={i}
						className={cn(FILLED_BOX, "h-14 animate-pulse")}
					/>
				))}
			</div>
		);
	}

	if (isError) {
		return (
			<p className="py-6 text-center text-sm text-destructive">
				Could not load recent activity.
			</p>
		);
	}

	if (displayedItems.length === 0) {
		return emptyState;
	}

	const list = (
		<ul className="space-y-1">
			{displayedItems.map((item, itemIndex) => (
				<li key={item.id}>
					<AttentionRow
						owner={owner}
						item={item}
						index={itemIndex}
						onFocus={() => setActiveIndex(itemIndex)}
						onOpenItem={onOpenItem}
						tone={tone}
						rowAction={rowAction}
					/>
				</li>
			))}
		</ul>
	);

	return (
		<section
			ref={attentionRef}
			data-attention-list
			aria-label={ariaLabel}
			aria-keyshortcuts={rowsOpen ? "ArrowDown ArrowUp Enter R" : undefined}
			className="space-y-2.5"
			onKeyDown={
				rowsOpen
					? (event) => {
							const target = event.target as HTMLElement;
							if (
								target.matches("input, textarea, select") ||
								target.isContentEditable
							) {
								return;
							}
							if (event.key === "ArrowDown" || event.key === "ArrowUp") {
								const focusedRow = target.closest<HTMLElement>(
									"[data-attention-index]",
								);
								if (!focusedRow) return;
								event.preventDefault();
								const focusedIndex = Number(focusedRow?.dataset.attentionIndex);
								const startingIndex = Number.isInteger(focusedIndex)
									? focusedIndex
									: event.key === "ArrowDown"
										? -1
										: 0;
								focusAttentionItem(
									startingIndex + (event.key === "ArrowDown" ? 1 : -1),
								);
							}
							if (event.key === "r" || event.key === "R") {
								const active = displayedItems[activeIndex];
								if (active?.unread && active.notificationId != null) {
									event.preventDefault();
									void markManyRead.mutateAsync([active.notificationId]);
								}
							}
						}
					: undefined
			}
		>
			{list}
			{rowsOpen ? (
				<div
					data-testid="attention-shortcut-hint"
					role="note"
					aria-label="Attention keyboard shortcuts"
					className="attention-shortcut-hint pointer-events-none mx-auto mt-3 w-fit items-center gap-4 rounded-full border bg-background/95 px-5 py-2 text-xs text-muted-foreground  backdrop-blur"
				>
					<span>
						<kbd className="rounded bg-muted px-1.5 py-0.5">↑↓</kbd> Navigate
					</span>
					<span>
						<kbd className="rounded bg-muted px-1.5 py-0.5">Enter</kbd> Open
					</span>
					<span>
						<kbd className="rounded bg-muted px-1.5 py-0.5">R</kbd> Mark read
					</span>
					<span>
						<kbd className="rounded bg-muted px-1.5 py-0.5">Tab</kbd> Actions
					</span>
				</div>
			) : null}
		</section>
	);
}
